import { useMemo } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import {
  Avatar,
  Button,
  Card,
  Descriptions,
  Empty,
  Progress,
  Space,
  Statistic,
  Tag,
  Table,
  Tooltip,
  Typography,
} from 'antd'
import type { TableColumnsType } from 'antd'
import {
  ClockCircleOutlined,
  CloudUploadOutlined,
  CommentOutlined,
  FlagOutlined,
  MessageOutlined,
  ReloadOutlined,
  UserOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons'
import type { Tables } from '@/lib/supabase'
import { useAuthStore } from '@/auth/session'
import {
  AnimeHiatusAction,
  isAnimeHiatusActive,
} from '@/components/AnimeHiatusAction'
import { AdminMobileList } from '@/components/AdminMobileList'
import { AdminRecordActions } from '@/components/AdminRecordActions'
import { useIsMobile } from '@/lib/hooks'
import { supabase } from '@/lib/supabase'
import { formatCST, nowCST } from '@/lib/time'

export const Route = createFileRoute('/_authed/dashboard')({
  component: Dashboard,
})

// DB update_weekday: 0=周一 ... 6=周日（见 backend migration 20260524233216 上的 column comment）
// dayjs().day(): 0=周日 ... 6=周六；用 nowCST() 确保按上海时区算今天周几
// 换算：(jsDay + 6) % 7
const WEEKDAY_NAMES = ['周一', '周二', '周三', '周四', '周五', '周六', '周日']
function todayWeekday(): number {
  return (nowCST().day() + 6) % 7
}

/** 卡片右上「刷新」：桌面带文字，手机收成 icon-only（Tooltip + aria-label） */
function CardRefreshButton({
  onClick,
  loading,
}: {
  onClick: () => void
  loading: boolean
}) {
  const isMobile = useIsMobile()
  if (isMobile) {
    return (
      <Tooltip title="刷新">
        <Button
          size="small"
          type="text"
          aria-label="刷新"
          icon={<ReloadOutlined />}
          onClick={onClick}
          loading={loading}
        />
      </Tooltip>
    )
  }
  return (
    <Button
      size="small"
      icon={<ReloadOutlined />}
      onClick={onClick}
      loading={loading}
    >
      刷新
    </Button>
  )
}

/** 移动端番剧卡片的封面缩略图（与桌面表格同尺寸口径，略小） */
function AnimeCover({ src }: { src: string | null }) {
  return (
    <Avatar
      shape="square"
      size={48}
      src={src ?? undefined}
      style={src ? undefined : { background: 'var(--ant-color-fill-tertiary)' }}
    />
  )
}

/** admin.get_dashboard_stats RPC 返回结构（仅 data 字段） */
interface DashboardStats {
  users: number
  animes: number
  animes_published: number
  comments: number
  comments_pending: number
  danmaku: number
  danmaku_pending: number
  reports_pending: number
}

type TodayAnime = Pick<
  Tables<'animes'>,
  | 'id'
  | 'title'
  | 'cover_url'
  | 'current_episodes'
  | 'total_episodes'
  | 'is_finished'
  | 'is_published'
  | 'view_count'
  | 'hiatus_until'
  | 'update_weekday'
>

/** private.migration_progress 视图聚合行（admin.get_migration_progress 包装返回） */
interface MigrationProgress {
  total: number
  migrated: number
  pending: number
  pct: number | string // numeric 经过 JSON 可能是 string
  last_7d: number
  last_24h: number
}

function Dashboard() {
  const claims = useAuthStore((s) => s.claims)
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const weekday = todayWeekday()

  const todayQuery = useQuery({
    queryKey: ['dashboard', 'today-updates', weekday],
    queryFn: async () => {
      // is_finished 三态（true/false/null），homepage RPC 用 coalesce(...,false)=false，
      // 这里等价拼成 .or('is_finished.is.null,is_finished.eq.false')
      const { data, error } = await supabase
        .from('animes')
        .select(
          'id, title, cover_url, current_episodes, total_episodes, is_finished, is_published, view_count, hiatus_until, update_weekday',
        )
        .eq('update_weekday', weekday)
        .or('is_finished.is.null,is_finished.eq.false')
        .order('is_published', { ascending: false })
        .order('view_count', { ascending: false, nullsFirst: false })
        .order('id', { ascending: false })
        .limit(100)
      if (error) throw error
      return data satisfies TodayAnime[]
    },
    staleTime: 60_000,
  })

  // 每个番剧"最近一次出集"：拉今日列表里所有 anime_id 的 main/movie 集，
  // 按 created_at 降序排好，客户端 group by anime_id 取第一条 = 最新一集。
  //
  // 为什么不用 animes.updated_at：那个字段也会被元数据编辑触发（admin 改封面 /
  // 标签 / 推荐位都算"改动"），不是"上次出集"的真实信号。
  //
  // 为什么不用 RPC：今日列表通常 5-30 部，每部均集数 12-24，总行数 < 1000，
  // 单次 PostgREST 拉完客户端 reduce 足够，没必要新写 RPC。
  const animeIds = useMemo(
    () => todayQuery.data?.map((a) => a.id) ?? [],
    [todayQuery.data],
  )
  const lastEpisodeQuery = useQuery({
    queryKey: ['dashboard', 'last-episode-at', animeIds],
    enabled: animeIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('episodes')
        .select('anime_id, created_at')
        .in('anime_id', animeIds)
        .in('kind', ['main', 'movie'])
        .order('created_at', { ascending: false })
        .limit(Math.max(animeIds.length * 200, 1000))
      if (error) throw error
      // 第一次见到的 anime_id 就是它的 max(created_at)
      const last = new Map<number, string>()
      for (const ep of data) {
        if (!ep.created_at) continue
        if (!last.has(ep.anime_id)) last.set(ep.anime_id, ep.created_at)
      }
      return last
    },
    staleTime: 60_000,
  })

  const todayStr = nowCST().format('YYYY-MM-DD')
  function isUpdatedToday(at: string | undefined): boolean {
    if (!at) return false
    return formatCST(at, 'YYYY-MM-DD') === todayStr
  }

  // ── 基础数据：单 RPC 拿全（users / animes / comments / danmaku / 各 pending）
  const statsQuery = useQuery({
    queryKey: ['dashboard', 'stats'],
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('get_dashboard_stats')
      if (error) throw error
      const r = data as { ok: boolean; error?: string; data?: DashboardStats }
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return r.data ?? null
    },
  })

  // ── 临时：legacy 用户迁移进度（admin.get_migration_progress 桥接 private 视图）
  // 迁移完成后，这块卡片 + backend RPC + private.migration_progress 视图都可以一起删
  const migrationQuery = useQuery({
    queryKey: ['dashboard', 'migration-progress'],
    staleTime: 60_000,
    queryFn: async () => {
      // admin schema 没生成进 database.types.ts，先 cast
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('get_migration_progress')
      if (error) throw error
      const r = data as {
        ok: boolean
        error?: string
        data?: MigrationProgress
      }
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return r.data ?? null
    },
  })

  // 已出的排到后面 + 视觉弱化（保留剩余顺序：未出按原排序）
  const sortedItems = useMemo(() => {
    const list = todayQuery.data ?? []
    const last = lastEpisodeQuery.data
    if (!last) return list
    return [...list].sort((a, b) => {
      const aDone = isUpdatedToday(last.get(a.id))
      const bDone = isUpdatedToday(last.get(b.id))
      if (aDone === bDone) return 0
      return aDone ? 1 : -1
    })
    // todayStr 跨日才变，不进依赖（项目没启用 react-hooks/exhaustive-deps 规则）
  }, [todayQuery.data, lastEpisodeQuery.data])

  const todayColumns = useMemo<TableColumnsType<TodayAnime>>(
    () => [
      {
        title: '番剧',
        key: 'anime',
        minWidth: 280,
        render: (_, item) => {
          const done = isUpdatedToday(lastEpisodeQuery.data?.get(item.id))
          return (
            <Space size={12} align="center">
              <Avatar
                shape="square"
                size={48}
                src={item.cover_url ?? undefined}
                style={
                  item.cover_url
                    ? undefined
                    : { background: 'var(--ant-color-fill-tertiary)' }
                }
              />
              <Space direction="vertical" size={2}>
                <Space size={6} wrap>
                  <Typography.Text
                    strong
                    style={{
                      textDecoration: done ? 'line-through' : undefined,
                    }}
                  >
                    {item.title}
                  </Typography.Text>
                  {item.is_published ? null : <Tag>草稿</Tag>}
                </Space>
                <Typography.Text
                  type="secondary"
                  style={{ fontVariantNumeric: 'tabular-nums', fontSize: 12 }}
                >
                  ID {item.id} · 浏览 {item.view_count ?? 0}
                </Typography.Text>
              </Space>
            </Space>
          )
        },
      },
      {
        title: '进度',
        key: 'progress',
        width: 130,
        render: (_, item) => (
          <Typography.Text style={{ fontVariantNumeric: 'tabular-nums' }}>
            {item.current_episodes ?? 0}
            {item.total_episodes != null ? ` / ${item.total_episodes}` : ''} 集
          </Typography.Text>
        ),
      },
      {
        title: '最近出集',
        key: 'lastEpisodeAt',
        width: 160,
        render: (_, item) => {
          const lastAt = lastEpisodeQuery.data?.get(item.id)
          if (lastEpisodeQuery.isLoading) {
            return <Typography.Text type="secondary">...</Typography.Text>
          }
          if (!lastAt) {
            return <Typography.Text type="secondary">尚未出集</Typography.Text>
          }
          const today = isUpdatedToday(lastAt)
          return (
            <Typography.Text
              type={today ? 'success' : 'secondary'}
              title={formatCST(lastAt)}
            >
              {today ? '今日已出 ' : '上次出集 '}
              {formatCST(lastAt, 'MM-DD HH:mm')}
            </Typography.Text>
          )
        },
      },
      {
        title: '状态',
        key: 'status',
        width: 150,
        render: (_, item) => (
          <Space size={4} wrap>
            <Tag color={item.is_published ? 'green' : 'default'}>
              {item.is_published ? '已发布' : '草稿'}
            </Tag>
            {isAnimeHiatusActive(item.hiatus_until) ? (
              <Tag
                color="warning"
                title={`停播至 ${formatCST(item.hiatus_until)}`}
              >
                停播至 {formatCST(item.hiatus_until, 'MM-DD')}
              </Tag>
            ) : null}
          </Space>
        ),
      },
      {
        title: '操作',
        key: 'actions',
        width: 230,
        fixed: 'right',
        render: (_, item) => (
          <Space size={4}>
            <Button
              type="primary"
              size="small"
              icon={<CloudUploadOutlined />}
              onClick={() =>
                navigate({
                  to: '/anime/$animeId/episodes',
                  params: { animeId: String(item.id) },
                  search: { add: 1 },
                })
              }
            >
              追更
            </Button>
            <Button
              size="small"
              onClick={() =>
                navigate({
                  to: '/anime/$animeId/episodes',
                  params: { animeId: String(item.id) },
                })
              }
            >
              剧集
            </Button>
            <AnimeHiatusAction
              animeId={item.id}
              hiatusUntil={item.hiatus_until}
              updateWeekday={item.update_weekday}
              extraInvalidateKeys={[['dashboard', 'today-updates', weekday]]}
            />
          </Space>
        ),
      },
    ],
    [lastEpisodeQuery.data, lastEpisodeQuery.isLoading, navigate],
  )

  const loginDescriptionItems = [
    {
      key: 'email',
      label: '邮箱',
      children: claims?.email,
    },
    {
      key: 'sub',
      label: 'User ID',
      children: (
        <Typography.Text
          copyable={{ text: claims?.sub }}
          style={{ fontSize: 12 }}
        >
          {claims?.sub}
        </Typography.Text>
      ),
    },
    {
      key: 'roles',
      label: '角色',
      children: (
        <Space size={4}>
          <Tag color={claims?.is_admin ? 'green' : 'default'}>
            admin: {String(claims?.is_admin ?? false)}
          </Tag>
          <Tag color={claims?.is_moderator ? 'blue' : 'default'}>
            moderator: {String(claims?.is_moderator ?? false)}
          </Tag>
        </Space>
      ),
    },
    {
      key: 'exp',
      label: 'Token 过期',
      children: claims?.exp ? formatCST(claims.exp * 1000) : '-',
    },
  ]

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-header__title">仪表盘</h1>
          <div className="admin-page-header__meta">
            {todayStr} · {WEEKDAY_NAMES[weekday]} · 数据每 60 秒缓存
          </div>
        </div>
        <Button
          icon={<ReloadOutlined />}
          onClick={() => {
            void Promise.all([
              statsQuery.refetch(),
              todayQuery.refetch(),
              migrationQuery.refetch(),
            ])
          }}
          loading={
            (statsQuery.isFetching ||
              todayQuery.isFetching ||
              migrationQuery.isFetching) &&
            !statsQuery.isLoading &&
            !todayQuery.isLoading &&
            !migrationQuery.isLoading
          }
        >
          刷新全部
        </Button>
      </div>

      <StatsCard query={statsQuery} navigate={navigate} />

      <Card
        className="admin-table-card"
        title={
          <Space size={8} align="center">
            <Typography.Title level={5} style={{ margin: 0 }}>
              今日更新
            </Typography.Title>
            <Tag color="processing" style={{ marginRight: 0 }}>
              {WEEKDAY_NAMES[weekday]}
            </Tag>
            {todayQuery.data ? (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {todayQuery.data.length} 部待更新
              </Typography.Text>
            ) : null}
          </Space>
        }
        extra={
          <CardRefreshButton
            onClick={() => void todayQuery.refetch()}
            loading={todayQuery.isFetching && !todayQuery.isLoading}
          />
        }
      >
        <div className="admin-table-wrap">
          <Table<TodayAnime>
            loading={todayQuery.isLoading}
            dataSource={sortedItems}
            columns={todayColumns}
            rowKey="id"
            size="small"
            pagination={false}
            scroll={{ x: 960 }}
            rowClassName={(item) =>
              isUpdatedToday(lastEpisodeQuery.data?.get(item.id))
                ? 'dashboard-today-row--done'
                : ''
            }
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <Typography.Text type="secondary">
                      今天没有未完结的番剧需要更新
                    </Typography.Text>
                  }
                  style={{ padding: '32px 0' }}
                />
              ),
            }}
          />
        </div>

        <AdminMobileList<TodayAnime>
          rows={sortedItems}
          rowKey={(item) => item.id}
          loading={todayQuery.isLoading}
          emptyContent={
            todayQuery.isLoading ? undefined : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="今天没有未完结的番剧需要更新"
              />
            )
          }
          renderItem={(item) => {
            const lastAt = lastEpisodeQuery.data?.get(item.id)
            const done = isUpdatedToday(lastAt)
            const openEpisodes = () =>
              navigate({
                to: '/anime/$animeId/episodes',
                params: { animeId: String(item.id) },
              })
            return (
              <article
                className={[
                  'admin-mobile-record',
                  done ? 'dashboard-today-row--done' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                role="button"
                tabIndex={0}
                aria-label={`${item.title}：打开剧集管理`}
                onClick={openEpisodes}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    openEpisodes()
                  }
                }}
              >
                <div className="admin-mobile-record__lead">
                  <AnimeCover src={item.cover_url} />
                  <div className="dashboard-mobile-anime__main">
                    <div className="admin-mobile-record__head">
                      <div
                        className="admin-mobile-record__title"
                        style={{
                          textDecoration: done ? 'line-through' : undefined,
                        }}
                      >
                        {item.title}
                      </div>
                      <Tag
                        color={item.is_published ? 'green' : 'default'}
                        style={{ marginInlineEnd: 0 }}
                      >
                        {item.is_published ? '已发布' : '草稿'}
                      </Tag>
                    </div>
                    <div className="admin-mobile-record__meta">
                      <span className="dashboard-mobile-anime__num">
                        {item.current_episodes ?? 0}
                        {item.total_episodes != null
                          ? ` / ${item.total_episodes}`
                          : ''}{' '}
                        集
                      </span>
                      <span>
                        {lastEpisodeQuery.isLoading ? (
                          '…'
                        ) : !lastAt ? (
                          '尚未出集'
                        ) : (
                          <Typography.Text
                            type={done ? 'success' : 'secondary'}
                            style={{ fontSize: 12 }}
                          >
                            {done ? '今日已出 ' : '上次出集 '}
                            {formatCST(lastAt, 'MM-DD HH:mm')}
                          </Typography.Text>
                        )}
                      </span>
                      <span className="dashboard-mobile-anime__num">
                        ID {item.id} · 浏览 {item.view_count ?? 0}
                      </span>
                    </div>
                    {isAnimeHiatusActive(item.hiatus_until) ? (
                      <div className="admin-mobile-record__badges">
                        <Tag color="warning">
                          停播至 {formatCST(item.hiatus_until, 'MM-DD')}
                        </Tag>
                      </div>
                    ) : null}
                  </div>
                </div>
                <AdminRecordActions
                  leading={
                    <AnimeHiatusAction
                      animeId={item.id}
                      hiatusUntil={item.hiatus_until}
                      updateWeekday={item.update_weekday}
                      extraInvalidateKeys={[
                        ['dashboard', 'today-updates', weekday],
                      ]}
                    />
                  }
                  actions={[
                    {
                      key: 'follow',
                      label: '追更',
                      icon: <CloudUploadOutlined />,
                      primary: true,
                      onClick: () =>
                        navigate({
                          to: '/anime/$animeId/episodes',
                          params: { animeId: String(item.id) },
                          search: { add: 1 },
                        }),
                    },
                  ]}
                />
              </article>
            )
          }}
        />
      </Card>

      <div className="admin-dashboard-grid">
        <StaleAnimesCard navigate={navigate} />

        <div className="admin-dashboard-side">
          {/* 临时：legacy 用户迁移进度。迁移完成后整块连同 backend RPC 删 */}
          <Card
            size="small"
            title={
              <Space size={8} align="center">
                <Typography.Text strong>用户迁移进度</Typography.Text>
                <Tag color="warning" style={{ marginRight: 0 }}>
                  临时
                </Tag>
              </Space>
            }
            extra={
              <CardRefreshButton
                onClick={() => void migrationQuery.refetch()}
                loading={migrationQuery.isFetching && !migrationQuery.isLoading}
              />
            }
          >
            {migrationQuery.isLoading ? (
              <Typography.Text type="secondary">加载中…</Typography.Text>
            ) : migrationQuery.error ? (
              <Typography.Text type="danger">
                {migrationQuery.error.message}
              </Typography.Text>
            ) : migrationQuery.data ? (
              (() => {
                const m = migrationQuery.data
                const pct = Number(m.pct) || 0
                return (
                  <div className="admin-migration-body">
                    <Progress
                      type="circle"
                      size={isMobile ? 80 : 96}
                      percent={pct}
                      format={(p) => `${p?.toFixed(1) ?? 0}%`}
                    />
                    <Space
                      direction="vertical"
                      size={8}
                      style={{ flex: 1, minWidth: 0 }}
                    >
                      <Statistic
                        title="已迁移"
                        value={m.migrated}
                        suffix={
                          <Typography.Text
                            type="secondary"
                            style={{ fontSize: 12 }}
                          >
                            / {m.total}
                          </Typography.Text>
                        }
                        valueStyle={{ fontVariantNumeric: 'tabular-nums' }}
                      />
                      <Statistic
                        title="待迁移"
                        value={m.pending}
                        valueStyle={{
                          fontVariantNumeric: 'tabular-nums',
                          color:
                            m.pending > 0
                              ? 'var(--ant-color-warning-text)'
                              : 'var(--ant-color-success-text)',
                        }}
                      />
                      <Space size="large" wrap>
                        <Statistic
                          title="近 7 天"
                          value={m.last_7d}
                          valueStyle={{
                            fontVariantNumeric: 'tabular-nums',
                            fontSize: 18,
                          }}
                        />
                        <Statistic
                          title="近 24 小时"
                          value={m.last_24h}
                          valueStyle={{
                            fontVariantNumeric: 'tabular-nums',
                            fontSize: 18,
                          }}
                        />
                      </Space>
                    </Space>
                  </div>
                )
              })()
            ) : (
              <Typography.Text type="secondary">无数据</Typography.Text>
            )}
          </Card>

          <Card size="small" title="登录信息">
            <Descriptions
              column={1}
              size="small"
              items={loginDescriptionItems}
            />
          </Card>
        </div>
      </div>
    </div>
  )
}

function StatsCard({
  query,
  navigate,
}: {
  query: ReturnType<typeof useQuery<DashboardStats | null, Error>>
  navigate: ReturnType<typeof useNavigate>
}) {
  const extra = (
    <CardRefreshButton
      onClick={() => void query.refetch()}
      loading={query.isFetching && !query.isLoading}
    />
  )

  // 加载 / 错误 / 空 三态 —— 外层用普通 Card 套个外壳即可
  if (query.isLoading) {
    return (
      <Card size="small" title="数据总览" extra={extra}>
        <Typography.Text type="secondary">加载中…</Typography.Text>
      </Card>
    )
  }
  if (query.error) {
    return (
      <Card size="small" title="数据总览" extra={extra}>
        <Typography.Text type="danger">{query.error.message}</Typography.Text>
      </Card>
    )
  }
  if (!query.data) {
    return (
      <Card size="small" title="数据总览" extra={extra}>
        <Typography.Text type="secondary">无数据</Typography.Text>
      </Card>
    )
  }

  const d = query.data
  const reportsHot = d.reports_pending > 0

  return (
    <Card className="admin-table-card" title="数据总览" extra={extra}>
      <div className="admin-kpi-band dashboard-kpi-band">
        <KpiCell
          icon={<UserOutlined />}
          label="用户"
          value={d.users}
          sub="注册总数"
        />
        <KpiCell
          icon={<VideoCameraOutlined />}
          label="番剧"
          value={d.animes}
          sub={`已发布 ${d.animes_published.toLocaleString('zh-CN')}`}
        />
        <KpiCell
          icon={<MessageOutlined />}
          label="评论"
          value={d.comments}
          sub={`待审 ${d.comments_pending.toLocaleString('zh-CN')}`}
        />
        <KpiCell
          icon={<CommentOutlined />}
          label="弹幕"
          value={d.danmaku}
          sub={`待审 ${d.danmaku_pending.toLocaleString('zh-CN')}`}
        />
        <div
          role="button"
          tabIndex={0}
          className="admin-kpi-cell admin-kpi-cell--action"
          onClick={() => navigate({ to: '/reports' })}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              navigate({ to: '/reports' })
            }
          }}
          title="点击进入举报受理页"
        >
          <Statistic
            title={
              <Space size={4} className="admin-kpi-label">
                <FlagOutlined />
                <span>待审举报</span>
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  ↗ 不含 vent
                </Typography.Text>
              </Space>
            }
            value={d.reports_pending}
            groupSeparator=","
            valueStyle={{
              fontVariantNumeric: 'tabular-nums',
              fontSize: 22,
              color: reportsHot
                ? 'var(--ant-color-warning-text)'
                : 'var(--ant-color-success-text)',
            }}
          />
        </div>
      </div>
    </Card>
  )
}

function KpiCell({
  icon,
  label,
  value,
  sub,
}: {
  icon: React.ReactNode
  label: string
  value: number
  sub: string
}) {
  return (
    <div className="admin-kpi-cell">
      <Statistic
        title={
          <div className="admin-kpi-label">
            {icon}
            <span>{label}</span>
          </div>
        }
        value={value}
        groupSeparator=","
        valueStyle={{ fontVariantNumeric: 'tabular-nums', fontSize: 28 }}
      />
      <div className="admin-kpi-sub">{sub}</div>
    </div>
  )
}

/* ── 超期未更新补番 ───────────────────────────────────────────────────
   调 admin.list_stale_unfinished_animes，dashboard 上只展示 top 10
   （stale_desc：最久没更新在前），点"追更"直跳到剧集编辑页 add=1，
   "查看全部"链到 /stale-animes 完整列表。
   ────────────────────────────────────────────────────────────────── */

const STALE_LIMIT = 10

interface StaleAnimeRow {
  id: number
  title: string
  title_original: string | null
  cover_url: string | null
  current_episodes: number | null
  total_episodes: number | null
  is_published: boolean | null
  is_finished: boolean | null
  update_weekday: number | null
  view_count: number | null
  hiatus_until: string | null
  last_update_at: string | null
  days_since: number | null
}

interface StaleListResult {
  ok: boolean
  total?: number
  rows?: Array<StaleAnimeRow>
  cutoff?: string
  days?: number
  error?: string
}

function daysTagColor(d: number | null): string {
  if (d === null) return 'red'
  if (d > 30) return 'red'
  if (d > 14) return 'orange'
  return 'gold'
}

function StaleAnimesCard({
  navigate,
}: {
  navigate: ReturnType<typeof useNavigate>
}) {
  const staleQuery = useQuery({
    queryKey: ['dashboard', 'stale-animes', STALE_LIMIT],
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('list_stale_unfinished_animes', {
          p_days: 8,
          p_sort: 'stale_desc',
          p_limit: STALE_LIMIT,
          p_offset: 0,
        })
      if (error) throw error
      const r = data as StaleListResult
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return {
        rows: r.rows ?? [],
        total: r.total ?? 0,
        days: r.days ?? 8,
      }
    },
  })

  const columns = useMemo<TableColumnsType<StaleAnimeRow>>(
    () => [
      {
        title: '番剧',
        key: 'anime',
        minWidth: 280,
        render: (_, item) => (
          <Space size={12} align="center">
            <Avatar
              shape="square"
              size={48}
              src={item.cover_url ?? undefined}
              style={
                item.cover_url
                  ? undefined
                  : { background: 'var(--ant-color-fill-tertiary)' }
              }
            />
            <Space direction="vertical" size={2}>
              <Space size={6} wrap>
                <Typography.Text strong>{item.title}</Typography.Text>
                {item.is_published ? null : <Tag>草稿</Tag>}
                {isAnimeHiatusActive(item.hiatus_until) ? (
                  <Tag
                    color="warning"
                    title={`停播至 ${formatCST(item.hiatus_until)}`}
                  >
                    停播至 {formatCST(item.hiatus_until, 'MM-DD')}
                  </Tag>
                ) : null}
              </Space>
              <Typography.Text
                type="secondary"
                style={{ fontVariantNumeric: 'tabular-nums', fontSize: 12 }}
              >
                ID {item.id}
                {item.title_original ? ` · ${item.title_original}` : ''}
              </Typography.Text>
            </Space>
          </Space>
        ),
      },
      {
        title: '进度',
        key: 'progress',
        width: 110,
        render: (_, item) => (
          <Typography.Text style={{ fontVariantNumeric: 'tabular-nums' }}>
            {item.current_episodes ?? 0}
            {item.total_episodes != null ? ` / ${item.total_episodes}` : ''} 集
          </Typography.Text>
        ),
      },
      {
        title: '最后更新',
        key: 'lastAt',
        width: 150,
        render: (_, item) =>
          item.last_update_at ? (
            <Typography.Text
              type="secondary"
              style={{ fontSize: 12 }}
              title={formatCST(item.last_update_at)}
            >
              {formatCST(item.last_update_at, 'YYYY-MM-DD')}
            </Typography.Text>
          ) : (
            <Tag color="red">从未更新</Tag>
          ),
      },
      {
        title: '距今',
        key: 'daysSince',
        width: 90,
        render: (_, item) => (
          /* 停播中的番超期是已知状态，天数降为中性色；异常感只留给没解释的 */
          <Tag
            color={
              isAnimeHiatusActive(item.hiatus_until)
                ? 'default'
                : daysTagColor(item.days_since)
            }
            style={{ fontVariantNumeric: 'tabular-nums', marginRight: 0 }}
          >
            {item.days_since === null ? '—' : `${item.days_since} 天`}
          </Tag>
        ),
      },
      {
        title: '操作',
        key: 'actions',
        width: 230,
        fixed: 'right',
        render: (_, item) => (
          <Space size={4}>
            <Button
              type="primary"
              size="small"
              icon={<CloudUploadOutlined />}
              onClick={() =>
                navigate({
                  to: '/anime/$animeId/episodes',
                  params: { animeId: String(item.id) },
                  search: { add: 1 },
                })
              }
            >
              追更
            </Button>
            <Button
              size="small"
              onClick={() =>
                navigate({
                  to: '/anime/$animeId/episodes',
                  params: { animeId: String(item.id) },
                })
              }
            >
              剧集
            </Button>
            <AnimeHiatusAction
              animeId={item.id}
              hiatusUntil={item.hiatus_until}
              updateWeekday={item.update_weekday}
              extraInvalidateKeys={[['dashboard', 'stale-animes', STALE_LIMIT]]}
            />
          </Space>
        ),
      },
    ],
    [navigate],
  )

  return (
    <Card
      className="admin-table-card"
      title={
        <Space size={8} align="center">
          <ClockCircleOutlined />
          <Typography.Title level={5} style={{ margin: 0 }}>
            超期未更新 · 补番
          </Typography.Title>
          {staleQuery.data ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              共 {staleQuery.data.total} 部 · 阈值 &gt; {staleQuery.data.days}{' '}
              天
            </Typography.Text>
          ) : null}
        </Space>
      }
      extra={
        <Space size={4}>
          <Link to="/stale-animes">
            <Button size="small" type="link">
              查看全部
            </Button>
          </Link>
          <CardRefreshButton
            onClick={() => void staleQuery.refetch()}
            loading={staleQuery.isFetching && !staleQuery.isLoading}
          />
        </Space>
      }
    >
      <div className="admin-table-wrap">
        <Table<StaleAnimeRow>
          loading={staleQuery.isLoading}
          dataSource={staleQuery.data?.rows ?? []}
          columns={columns}
          rowKey="id"
          size="small"
          pagination={false}
          scroll={{ x: 900 }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Typography.Text type="secondary">
                    没有超期未更新的番剧
                  </Typography.Text>
                }
                style={{ padding: '32px 0' }}
              />
            ),
          }}
        />
      </div>

      <AdminMobileList<StaleAnimeRow>
        rows={staleQuery.data?.rows ?? []}
        rowKey={(item) => item.id}
        loading={staleQuery.isLoading}
        emptyContent={
          staleQuery.isLoading ? undefined : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="没有超期未更新的番剧"
            />
          )
        }
        renderItem={(item) => {
          const hiatus = isAnimeHiatusActive(item.hiatus_until)
          const openEpisodes = () =>
            navigate({
              to: '/anime/$animeId/episodes',
              params: { animeId: String(item.id) },
            })
          return (
            <article
              className="admin-mobile-record"
              role="button"
              tabIndex={0}
              aria-label={`${item.title}：打开剧集管理`}
              onClick={openEpisodes}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  openEpisodes()
                }
              }}
            >
              <div className="admin-mobile-record__lead">
                <AnimeCover src={item.cover_url} />
                <div className="dashboard-mobile-anime__main">
                  <div className="admin-mobile-record__head">
                    <div className="admin-mobile-record__title">
                      {item.title}
                    </div>
                    {/* 停播中的番超期是已知状态，天数降为中性色 */}
                    <Tag
                      color={hiatus ? 'default' : daysTagColor(item.days_since)}
                      className="dashboard-mobile-anime__num"
                      style={{ marginInlineEnd: 0 }}
                    >
                      {item.days_since === null ? '—' : `${item.days_since} 天`}
                    </Tag>
                  </div>
                  {item.title_original ? (
                    <Typography.Text
                      type="secondary"
                      ellipsis
                      className="dashboard-mobile-anime__sub"
                    >
                      {item.title_original}
                    </Typography.Text>
                  ) : null}
                  <div className="admin-mobile-record__meta">
                    <span className="dashboard-mobile-anime__num">
                      {item.current_episodes ?? 0}
                      {item.total_episodes != null
                        ? ` / ${item.total_episodes}`
                        : ''}{' '}
                      集
                    </span>
                    <span>
                      {item.last_update_at
                        ? `最后更新 ${formatCST(item.last_update_at, 'YYYY-MM-DD')}`
                        : '从未更新'}
                    </span>
                    <span className="dashboard-mobile-anime__num">
                      ID {item.id}
                    </span>
                  </div>
                  {!item.is_published || hiatus ? (
                    <div className="admin-mobile-record__badges">
                      {item.is_published ? null : <Tag>草稿</Tag>}
                      {hiatus ? (
                        <Tag color="warning">
                          停播至 {formatCST(item.hiatus_until, 'MM-DD')}
                        </Tag>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
              <AdminRecordActions
                leading={
                  <AnimeHiatusAction
                    animeId={item.id}
                    hiatusUntil={item.hiatus_until}
                    updateWeekday={item.update_weekday}
                    extraInvalidateKeys={[
                      ['dashboard', 'stale-animes', STALE_LIMIT],
                    ]}
                  />
                }
                actions={[
                  {
                    key: 'follow',
                    label: '追更',
                    icon: <CloudUploadOutlined />,
                    primary: true,
                    onClick: () =>
                      navigate({
                        to: '/anime/$animeId/episodes',
                        params: { animeId: String(item.id) },
                        search: { add: 1 },
                      }),
                  },
                ]}
              />
            </article>
          )
        }}
      />
    </Card>
  )
}
